"use no memo";

import { mergeProps, useRender } from "@base-ui/react";
import { Spinner } from "@choose/ui/spinner";
import { flexRender } from "@tanstack/react-table";
import type { Row, Table } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { VirtualItem } from "@tanstack/react-virtual";
import { cn } from "cn";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useDataList } from "~/components/ui/data-list/data-list";
import type { DataListFeatures } from "~/components/ui/data-list/data-list";

/**
 * Nearest scroll-area viewport that belongs to THIS grid. A viewport outside
 * the grid's own container (e.g. a page-level ScrollArea) would make the
 * width measurement - and the virtualizer - bind the wrong box.
 */
export function getDataListScrollAreaViewport(
  node: HTMLElement,
): HTMLElement | null {
  const scrollViewport = node.closest('[data-slot="scroll-area-viewport"]');

  if (!scrollViewport) return null;

  return scrollViewport as HTMLElement;
}

export interface DataListVirtualScrollElements {
  containerElement: HTMLDivElement | null;
  scrollElement: HTMLElement | null;
}

export function DataListVirtualStatusRow({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "text-muted-foreground py-4 text-center text-sm",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function DataListVirtualSpacer({ height }: { height: number }) {
  if (height <= 0) return null;

  return (
    <div aria-hidden style={{ height }}>
      {null}
    </div>
  );
}

export function DataListRow<TData extends object>({
  children,
  row,
  rowIndex,
  ...props
}: {
  children: ReactNode;
  row: Row<DataListFeatures, TData>;
  /** Virtualized list index, rendered as data-index for measureElement. */
  rowIndex?: number;
} & useRender.ComponentProps<"div">) {
  const { renderRow } = useDataList();

  const element = useRender({
    defaultTagName: "div",
    render: renderRow ? renderRow({ id: row.id, children }) : undefined,
    props: mergeProps<"div">(
      {
        ["data-index" as string]: rowIndex,
        ["data-row-id" as string]: row.id,
        ["data-slot" as string]: "data-list-row",
        className:
          "bg-background flex flex-col hover:bg-sidebar w-full h-11 focus:shadow-[0_0_0_1px_var(--color-selected)_inset] focus:outline-none",
        children,
      },
      props,
    ),
  });
  return element;
}

export function DataListRowContent<TData extends object>({
  row,
}: {
  row: Row<DataListFeatures, TData>;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center p-2 pr-4 pl-4 has-data-[list-grid-column=select]:pl-2">
      <div className="flex w-full flex-[initial] flex-row items-center gap-2 text-sm">
        {row.getVisibleCells().map((cell) => (
          <Fragment key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function DataListVirtualBody<TData extends object>({
  isFetchingMore,
  isInfiniteMode,
  loadingMoreMessage,
  measureRowRef,
  rows,
  //table,
  totalSize,
  virtualItems,
}: {
  isFetchingMore: boolean;
  isInfiniteMode: boolean;
  loadingMoreMessage: ReactNode;
  measureRowRef?: (element: HTMLDivElement | null) => void;
  rows: Row<DataListFeatures, TData>[];
  table: Table<DataListFeatures, TData>;
  totalSize: number;
  virtualItems: VirtualItem[];
}) {
  /*const { isLoading } = useDataList();

  if (isLoading) {
    const visibleColumns = table.getVisibleLeafColumns();
    return Array.from({ length: 10 }).map((_, rowIndex) => (
      <div
        key={rowIndex}
        className="bg-background hover:bg-sidebar flex w-full flex-col p-4 not-last:border-b focus:shadow-[0_0_0_1px_var(--color-selected)_inset] focus:outline-none"
      >
        <DataListRowSkeletonContent columns={visibleColumns} />
      </div>
    ));
  }*/

  const showFetchingRow = isInfiniteMode && isFetchingMore;

  const leadingSpacerHeight =
    virtualItems.length > 0 ? (virtualItems[0]?.start ?? 0) : 0;
  const trailingSpacerHeight =
    virtualItems.length > 0
      ? Math.max(
          0,
          totalSize - (virtualItems[virtualItems.length - 1]?.end ?? 0),
        )
      : 0;

  const renderedRows: ReactNode[] = [];

  if (leadingSpacerHeight > 0) {
    renderedRows.push(
      <DataListVirtualSpacer
        key="virtual-spacer-start"
        height={leadingSpacerHeight}
      />,
    );
  }

  virtualItems.forEach((virtualRow) => {
    const row = rows[virtualRow.index];

    if (!row) return;

    renderedRows.push(
      <DataListRow
        key={row.id}
        row={row}
        ref={measureRowRef}
        rowIndex={virtualRow.index}
      >
        <DataListRowContent row={row} />
      </DataListRow>,
    );
  });

  if (trailingSpacerHeight > 0) {
    renderedRows.push(
      <DataListVirtualSpacer
        key="virtual-spacer-end"
        height={trailingSpacerHeight}
      />,
    );
  }

  if (showFetchingRow) {
    renderedRows.push(
      <DataListVirtualStatusRow key="virtual-status-loading">
        <div className="flex items-center justify-center gap-2">
          <Spinner className="size-4 opacity-60" />
          {loadingMoreMessage}
        </div>
      </DataListVirtualStatusRow>,
    );
  }

  return <>{renderedRows}</>;
}

export function DataListTableBase({
  className,
  children,
  style,
}: {
  className?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      data-slot="data-list-table"
      className={cn("relative isolate overflow-clip", className)}
      role="table"
      style={style}
    >
      {children}
    </div>
  );
}

export function DataListTableBody({ children }: { children: ReactNode }) {
  return (
    <div data-slot="data-list-table-body" className="contents">
      {children}
    </div>
  );
}

export function DataListVirtual<TData extends object>({
  estimateSize = 44,
  hasMore,
  isFetchingMore = false,
  onFetchMore,
  overscan = 10,
  table,
}: {
  estimateSize?: number;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onFetchMore?: () => void;
  overscan?: number;
  table: Table<DataListFeatures, TData>;
}) {
  const { t } = useTranslation();
  const { isLoading } = useDataList();
  const isInfiniteMode = typeof onFetchMore === "function";

  const [viewportElements, setViewportElements] =
    useState<DataListVirtualScrollElements>({
      containerElement: null,
      scrollElement: null,
    });

  const rows = table.getRowModel().rows;

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => estimateSize,
    getScrollElement: () => viewportElements.scrollElement,
    overscan: overscan,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // Latch onFetchMore per row count: virtualItems gets a new identity every
  // scroll frame, so without it the effect fires duplicate page requests
  // before the consumer flips isFetchingMore, and loops at end-of-data when
  // hasMore is never set.
  const fetchMoreFiredAtCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isInfiniteMode || hasMore === false || isFetchingMore) return;

    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;

    if (fetchMoreFiredAtCountRef.current === rows.length) return;

    if (lastItem.index >= rows.length - 1) {
      fetchMoreFiredAtCountRef.current = rows.length;
      onFetchMore();
    }
  }, [
    hasMore,
    isFetchingMore,
    isInfiniteMode,
    onFetchMore,
    rows.length,
    virtualItems,
  ]);

  const handleViewportRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    setViewportElements({
      containerElement: node,
      scrollElement: getDataListScrollAreaViewport(node) ?? node,
    });
  }, []);

  return (
    <div
      ref={handleViewportRef}
      style={{
        width: "100%",
        height: isLoading ? "auto" : `${virtualizer.getTotalSize()}px`,
      }}
    >
      <DataListTableBase>
        <DataListTableBody>
          <DataListVirtualBody
            isFetchingMore={isFetchingMore}
            isInfiniteMode={isInfiniteMode}
            loadingMoreMessage={t("data_list.loading")}
            measureRowRef={virtualizer.measureElement}
            rows={rows}
            table={table}
            totalSize={totalSize}
            virtualItems={virtualItems}
          />
        </DataListTableBody>
      </DataListTableBase>
    </div>
  );
}
